import * as React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Divider from "@mui/joy/Divider";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Stack from "@mui/joy/Stack";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Typography from "@mui/joy/Typography";
import Card from "@mui/joy/Card";
import CardActions from "@mui/joy/CardActions";
import CardOverflow from "@mui/joy/CardOverflow";
import { db, storage } from "../../FirebaseConfig";
import firebase from "firebase/compat/app";
import { Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { UserAuth } from "../../context/AuthContext";
import Textarea from "@mui/joy/Textarea";
import { doc, setDoc, increment } from "firebase/firestore";

const RequiredLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <FormLabel>
    {children} <span style={{ color: "red" }}>*</span>
  </FormLabel>
);

const initialState = {
  id: "",
  reporttitle: "",
  vulnerabilitytype: "",
  severitylevel: "",
  affectedcomponent: "",
  reportdescription: "",
  stepstoreproduce: "",
  fileUrl: "",
  status: "In Review",
};

interface Props {
  eventname: string;
}

const ReportSubmission = ({ eventname, ...props }: Props) => {
  const { userData } = UserAuth();
  const [data, setData] = React.useState(initialState);
  const [fileLoading, setFileLoading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [uploadFile, setUploadFile] = React.useState("");
  const uid = uuidv4();

  const handleChange = (e: any) => {
    if (!e || !e.target) {
      return;
    }

    const { id, name, value } = e.target;
    const key = id || name;
    setData((prevData: any) => ({
      ...prevData,
      [key]: value,
    }));
  };

  const handleSeveritySelectChange = (
    event: React.SyntheticEvent | null,
    newValue: string | null
  ) => {
    if (newValue) {
      setData((prevData: any) => ({
        ...prevData,
        severitylevel: newValue,
      }));
    }
  };

  const handlePostFile = async (e: any) => {
    const file = e.target.files[0];

    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed.");
      return;
    }

    setFileLoading(true);

    // Reference to your Firebase Storage bucket
    const storageRef = storage.ref();
    const fileRef = storageRef.child(`Report-files/${file.name}`);

    const uploadTask = fileRef.put(file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(progress);
      },
      (error) => {
        console.error(error);
      },
      () => {
        uploadTask.snapshot.ref.getDownloadURL().then((downloadURL) => {
          setFileLoading(false);
          setUploadFile(downloadURL);
          toast.success("File Uploaded Successfully");
        });
      }
    );
  };

  const validateForm = () => {
    return (
      data.reporttitle &&
      data.vulnerabilitytype &&
      data.severitylevel &&
      data.affectedcomponent &&
      data.reportdescription &&
      data.stepstoreproduce &&
      uploadFile
    );
  };

  const updateStatistics = async (eventname: string, userName: any) => {
    const statsRef = db
      .collection("Events")
      .doc(eventname.toLowerCase())
      .collection("Statistics")
      .doc("reportStats");

    const severityStatsRef = doc(
      db,
      `Events/${eventname.toLowerCase()}/Statistics`,
      "severityStats"
    );

    const participantStatsRef = doc(
      db,
      `Events/${eventname.toLowerCase()}/Statistics`,
      "participantStats"
    );

    try {
      await db.runTransaction(async (transaction) => {
        // Update general statistics
        const statsDoc = await transaction.get(statsRef);

        if (!statsDoc.exists) {
          transaction.set(statsRef, {
            Accepted: 0,
            Rejected: 0,
            "In Review": 1, // Initialize with 1 pending report
          });
        } else {
          const currentData = statsDoc.data();
          transaction.update(statsRef, {
            "In Review": (currentData?.["In Review"] || 0) + 1, // Increment pending count
          });
        }

        // Update severity statistics
        await setDoc(
          severityStatsRef,
          {
            [data.severitylevel]: increment(1),
          },
          { merge: true }
        );

        // Update participant statistics
        await setDoc(
          participantStatsRef,
          {
            [userName]: increment(1),
          },
          { merge: true }
        );
      });
      
    } catch (error) {
      toast.error("Failed to update report statistics.");
    }
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill out all fields and upload a PDF file.");
      return;
    }

    data.fileUrl = uploadFile;

    try {
      await db
        .collection("Events")
        .doc(eventname.toLowerCase())
        .collection("Reports")
        .doc(uid)
        .set({
          id: uid,
          reporttitle: data.reporttitle,
          vulnerabilitytype: data.vulnerabilitytype,
          severitylevel: data.severitylevel,
          affectedcomponent: data.affectedcomponent,
          reportdescription: data.reportdescription,
          stepstoreproduce: data.stepstoreproduce,
          fileUrl: uploadFile,
          submittedAt: firebase.firestore.FieldValue.serverTimestamp(),
          submittedBy: userData?.email,
          submittedById: userData?.id,
          submittedByName: userData?.name,
          status: "In Review",
        });

      await updateStatistics(eventname, userData?.name);

      toast.success("Report Submitted Successfully");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error("Error submitting report. Please try again.");
    }
  };

  return (
    <Box sx={{ flex: 1, width: "100%" }}>
      <Stack
        spacing={4}
        sx={{
          display: "flex",
          mx: "auto",
          py: { xs: 2, md: 3 },
        }}
      >
        <Card>
          <Box sx={{ mb: 1 }}>
            <Typography level="title-md"> Submit a New Report</Typography>
            <Typography level="body-sm">
              Fill out the form to submit a new report.
            </Typography>
          </Box>
          <Divider />
          <Stack
            direction="row"
            spacing={3}
            sx={{ display: { xs: "none", md: "flex" }, my: 1 }}
          >
            <Stack spacing={2} sx={{ flexGrow: 1 }}>
              <RequiredLabel>
                <FormLabel>Report Title</FormLabel>
              </RequiredLabel>
              <Input
                size="sm"
                id="reporttitle"
                onChange={handleChange}
                placeholder="Enter the title of the report"
                required
              />
              <RequiredLabel>
                <FormLabel>Vulnerability Type</FormLabel>
              </RequiredLabel>
              <Input
                size="sm"
                id="vulnerabilitytype"
                onChange={handleChange}
                placeholder="Type of Vulnerability (for example: XSS, CSRF, etc.)"
                required
              />
              <RequiredLabel>
                <FormLabel> Severity Level </FormLabel>
              </RequiredLabel>
              <Select
                id="severitylevel"
                size="sm"
                onChange={handleSeveritySelectChange}
                placeholder="Choose the Severity Level"
                required
              >
                <Option value="Low">
                  <Typography textColor="text.tertiary" ml={0.5}>
                    Low
                  </Typography>
                </Option>
                <Option value="Medium">
                  <Typography textColor="text.tertiary" ml={0.5}>
                    Medium
                  </Typography>
                </Option>
                <Option value="High">
                  <Typography textColor="text.tertiary" ml={0.5}>
                    High
                  </Typography>
                </Option>
                <Option value="Critical">
                  <Typography textColor="text.tertiary" ml={0.5}>
                    Critical
                  </Typography>
                </Option>
              </Select>
              <RequiredLabel>
                <FormLabel> Affected URL/Component </FormLabel>
              </RequiredLabel>
              <Input
                size="sm"
                id="affectedcomponent"
                onChange={handleChange}
                placeholder="Enter the URL or component affected"
                required
              />
              <RequiredLabel>
                <FormLabel> Report Description </FormLabel>
              </RequiredLabel>
              <Textarea
                size="sm"
                name="reportdescription"
                onChange={handleChange}
                placeholder="Provide a detailed description of the vulnerability"
                required
              />
              <RequiredLabel>
                <FormLabel>
                  {" "}
                  Steps to Reproduce (Note: Use Comma to separate each steps)
                </FormLabel>
              </RequiredLabel>
              <Textarea
                size="sm"
                name="stepstoreproduce"
                onChange={handleChange}
                placeholder="Provide step-by-step instructions to reproduce the vulnerability"
                required
              />
              <RequiredLabel>
                <FormLabel>
                  Attachments (Note: Only PDF files are allowed)
                </FormLabel>
              </RequiredLabel>
              <Input size="sm" type="file" onChange={handlePostFile} required />
            </Stack>
          </Stack>
          {fileLoading === true ? (
            <h4>Uploading File {progress.toFixed(2)} %</h4>
          ) : (
            <></>
          )}
          <CardOverflow sx={{ borderTop: "1px solid", borderColor: "divider" }}>
            <CardActions sx={{ alignSelf: "flex-end", pt: 2 }}>
              <Button
                component={Link}
                to="/dashboard"
                size="sm"
                variant="outlined"
                color="neutral"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="outlined"
                onClick={onSubmit}
                disabled={!validateForm() || fileLoading}
              >
                Save
              </Button>
            </CardActions>
          </CardOverflow>
        </Card>
      </Stack>
    </Box>
  );
};

export default ReportSubmission;